/**
 * Settings sidebar navigation configuration.
 *
 * Defines all settings groups and sections. Each section has an id, label,
 * optional icon, optional gate (feature flag + permission), and a component
 * to render when active.
 */

import {
  Bell01,
  CalendarCheck01,
  CreditCard01,
  CurrencyDollar,
  Database01,
  Dataflow03,
  Download01,
  File06,
  Globe02,
  Key01,
  Lock01,
  Mail01,
  MessageChatSquare,
  Monitor04,
  Phone01,
  Server01,
  Settings01,
  ShieldTick,
  Stars02,
  Terminal,
  Upload01,
  User01,
  Users01,
  VideoRecorder,
  Zap,
} from '@untitledui/icons';
import type { ComponentType } from 'react';

import { ApiAccessSection } from '@/lib/api-access/api-access-section';
import { PipelineIcon } from '@/lib/crm/utils/icons/CustomIcons';
import { ActiveCampaignIcon } from '@/lib/integrations/activecampaign/ActiveCampaignIcon';
import { ActiveCampaignSection } from '@/lib/integrations/activecampaign/ActiveCampaignSection';
import { HubspotIcon } from '@/lib/integrations/hubspot/HubspotIcon';
import { HubspotSection } from '@/lib/integrations/hubspot/HubspotSection';
import { MailchimpIcon } from '@/lib/integrations/mailchimp/MailchimpIcon';
import { MailchimpSection } from '@/lib/integrations/mailchimp/MailchimpSection';
import { SalesforceIcon } from '@/lib/integrations/salesforce/SalesforceIcon';
import { SalesforceSection } from '@/lib/integrations/salesforce/SalesforceSection';
import { StripeIcon } from '@/lib/integrations/stripe/StripeIcon';
import { StripeSection } from '@/lib/integrations/stripe/StripeSection';
import { WhatsAppIcon } from '@/lib/integrations/whatsapp/WhatsAppIcon';
import { WhatsAppSection } from '@/lib/integrations/whatsapp/WhatsAppSection';
import SnowflakeSection from '@/lib/portfolio/components/SnowflakeSection';
import SocialConnections from '@/pages/auth/SocialConnections';

import { ActionRoutingSection } from './sections/action-routing-section';
import { AgentAutonomySection } from './sections/agent-autonomy-section';
import { AgentGuidanceSection } from './sections/agent-guidance-section';
import { AiActionsSection } from './sections/ai-actions-section';
import { APIKeysSection } from './sections/api-keys-section';
import { AutomationsSection } from './sections/automations-section';
import { BillingSection } from './sections/billing-section';
import { CrmDefaultsSection } from './sections/crm-defaults-section';
import { DesktopAppSection } from './sections/desktop-app-section';
import { EmailCalendarSection } from './sections/email-calendar-section';
import { EmailSignatureSection } from './sections/email-signature-section';
import { ExportSection } from './sections/export-section';
import { GeneralSection } from './sections/general-section';
import { ImportSection } from './sections/import-section';
import { McpSection } from './sections/mcp-section';
import { MeetingAssistantSection } from './sections/meeting-assistant-section';
import { MeetingSummariesSection } from './sections/meeting-summaries-section';
import { NotetakerSection } from './sections/notetaker-section';
import { NotificationsSection } from './sections/notifications-section';
import { ObjectsFieldsSection } from './sections/objects-fields-section';
import { PersonalInfoSection } from './sections/personal-info-section';
import { PhoneSection } from './sections/phone-section';
import { PipelinesSection } from './sections/pipelines-section';
import { SecuritySection } from './sections/security-section';
import { SlackSection } from './sections/slack-section';
import { TeamMembersSection } from './sections/team-members-section';
import { TeamsSection } from './sections/teams-section';
import { ValidationRulesSection } from './sections/validation-rules-section';
import { WebhooksSection } from './sections/webhooks-section';
import { WebsitesSection } from './sections/websites-section';
import {
  BILLING_ADMIN_PERMISSION,
  CRM_ADMIN_PERMISSION,
  TEAMS_ADMIN_PERMISSION,
} from './settings-permissions';

export interface SettingsNavConfigItem {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: ComponentType<any>;
  // NOTE: this shape is duplicated as `SettingsGate` in `use-settings-gate.ts`
  // (and a narrower third copy in `settings-mobile-list.tsx`). It cannot simply
  // import that type — `use-settings-gate.ts` imports the permission constants
  // from this file, so the dependency would be circular. Keep the three in
  // sync by hand until someone extracts the constants.
  gate?: {
    featureFlag?: string;
    permission?: string;
    readEntityType?: string;
    /** Inverse of `featureFlag` — hide from the nav while this flag is ON. */
    hiddenWhenFlag?: string;
  };
  /**
   * Whether this section is hidden from the sidebar nav but still accessible
   * via direct URL (e.g. the Advanced section).
   */
  hidden?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: ComponentType<any>;
}

export interface SettingsNavGroup {
  header: string;
  items: SettingsNavConfigItem[];
}

// Re-exported for backward compatibility — defined in settings-permissions.ts.
export {
  BILLING_ADMIN_PERMISSION,
  CRM_ADMIN_PERMISSION,
  TEAMS_ADMIN_PERMISSION,
} from './settings-permissions';

/**
 * All settings navigation groups and their sections.
 *
 * Groups order: Account → AI → Integrations → Contact sync → CRM → Workspace
 * Hidden sections (not in any group) are appended at the end.
 */
export const SETTINGS_NAV_GROUPS: SettingsNavGroup[] = [
  {
    header: 'Account',
    items: [
      {
        id: 'personal-info',
        label: 'Personal Info',
        icon: User01,
        component: PersonalInfoSection,
      },
      {
        id: 'agent-guidance',
        label: 'Agent Guidance',
        icon: Zap,
        component: AgentGuidanceSection,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        icon: Bell01,
        component: NotificationsSection,
      },
      {
        // id kept as 'email-signature' so the /settings/email-signature URL and
        // any bookmarks stay valid; the label is the user-facing name.
        id: 'email-signature',
        label: 'Email',
        icon: Mail01,
        component: EmailSignatureSection,
      },
      {
        id: 'security',
        label: 'Security',
        icon: Lock01,
        component: SecuritySection,
      },
      {
        id: 'api-keys',
        label: 'API Keys',
        icon: Key01,
        gate: { featureFlag: 'api_keys' },
        component: APIKeysSection,
      },
    ],
  },
  {
    header: 'AI',
    items: [
      {
        id: 'ai-actions',
        label: 'AI Actions',
        icon: Stars02,
        component: AiActionsSection,
      },
      {
        id: 'action-routing',
        label: 'Action Routing',
        icon: Users01,
        component: ActionRoutingSection,
      },
      {
        // The whole page is the auto-approval feature: without the flag the
        // org's agents never act on their own, so the entry disappears rather
        // than rendering an empty section.
        id: 'agent-autonomy',
        label: 'Agent Autonomy',
        icon: Zap,
        gate: { featureFlag: 'auto_approve_creation' },
        component: AgentAutonomySection,
      },
      {
        id: 'notetaker',
        label: 'Notetaker',
        icon: VideoRecorder,
        gate: { featureFlag: 'recall_notetaker' },
        component: NotetakerSection,
      },
    ],
  },
  {
    header: 'Integrations',
    items: [
      {
        id: 'email-calendar',
        label: 'Email & Calendar',
        icon: Mail01,
        component: EmailCalendarSection,
      },
      {
        id: 'slack',
        label: 'Slack',
        icon: MessageChatSquare,
        gate: { featureFlag: 'slack_sync' },
        component: SlackSection,
      },
      {
        id: 'phone',
        label: 'Phone',
        icon: Phone01,
        gate: { featureFlag: 'quo_sync' },
        component: PhoneSection,
      },
      {
        id: 'stripe',
        label: 'Stripe',
        icon: StripeIcon,
        gate: { featureFlag: 'stripe_sync' },
        component: StripeSection,
      },
      {
        id: 'whatsapp',
        label: 'WhatsApp',
        icon: WhatsAppIcon,
        gate: { featureFlag: 'whatsapp_sync' },
        component: WhatsAppSection,
      },
      {
        id: 'meeting-summaries',
        label: 'Meeting Summaries',
        icon: File06,
        component: MeetingSummariesSection,
      },
      {
        id: 'desktop-app',
        label: 'Ahoy Desktop',
        icon: Monitor04,
        gate: { featureFlag: 'ahoy_desktop_app' },
        component: DesktopAppSection,
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        icon: Dataflow03,
        gate: { featureFlag: 'inbound_webhooks' },
        component: WebhooksSection,
      },
      {
        id: 'mcp',
        label: 'MCP',
        icon: Terminal,
        gate: { featureFlag: 'mcp_oauth' },
        component: McpSection,
      },
      {
        // Kept as 'api-access' so the /settings/api-access URL and any
        // bookmarks stay valid; the label is the user-facing name.
        id: 'api-access',
        label: 'Service Accounts',
        icon: Server01,
        gate: { featureFlag: 'service_account' },
        component: ApiAccessSection,
      },
      {
        id: 'snowflake',
        label: 'Snowflake',
        icon: Database01,
        gate: { featureFlag: 'service_account' },
        component: SnowflakeSection,
      },
      {
        id: 'websites',
        label: 'Websites',
        icon: Globe02,
        gate: { featureFlag: 'form_collection' },
        component: WebsitesSection,
      },
    ],
  },
  {
    header: 'Contact sync',
    items: [
      {
        id: 'activecampaign',
        label: 'ActiveCampaign',
        icon: ActiveCampaignIcon,
        gate: { featureFlag: 'activecampaign_sync' },
        component: ActiveCampaignSection,
      },
      {
        id: 'mailchimp',
        label: 'Mailchimp',
        icon: MailchimpIcon,
        gate: { featureFlag: 'mailchimp_sync' },
        component: MailchimpSection,
      },
      {
        id: 'hubspot',
        label: 'HubSpot',
        icon: HubspotIcon,
        gate: { featureFlag: 'hubspot_sync' },
        component: HubspotSection,
      },
      {
        id: 'salesforce',
        label: 'Salesforce',
        icon: SalesforceIcon,
        gate: { featureFlag: 'salesforce_sync' },
        component: SalesforceSection,
      },
    ],
  },
  {
    header: 'CRM',
    items: [
      {
        id: 'objects-fields',
        label: 'Objects & Fields',
        icon: Settings01,
        component: ObjectsFieldsSection,
      },
      {
        id: 'pipelines',
        label: 'Pipelines',
        icon: PipelineIcon,
        gate: { permission: CRM_ADMIN_PERMISSION },
        component: PipelinesSection,
      },
      {
        id: 'meeting-assistant',
        label: 'Scheduling',
        icon: CalendarCheck01,
        component: MeetingAssistantSection,
      },
      {
        id: 'automations',
        label: 'Automations',
        icon: Dataflow03,
        // Automations move to their own top-level nav entry when builder mode
        // is on, so the settings entry disappears rather than duplicating it.
        // The URL keeps resolving — ProfilePage redirects it to /automations —
        // so existing links and bookmarks still work.
        gate: { hiddenWhenFlag: 'builder_mode' },
        component: AutomationsSection,
      },
      {
        id: 'validation-rules',
        label: 'Validation Rules',
        icon: ShieldTick,
        // Visible to anyone with read access to validation rules (ahoy_default
        // grants read); create / edit / toggle stay gated on write permission
        // inside the section itself.
        gate: {
          featureFlag: 'validation_rules',
          readEntityType: 'ahoy_validation_rule',
        },
        component: ValidationRulesSection,
      },
      {
        id: 'crm-defaults',
        label: 'Defaults',
        icon: CurrencyDollar,
        component: CrmDefaultsSection,
      },
      {
        id: 'import',
        label: 'Import',
        icon: Download01,
        component: ImportSection,
      },
      {
        id: 'export',
        label: 'Export',
        icon: Upload01,
        component: ExportSection,
      },
    ],
  },
  {
    header: 'Workspace',
    items: [
      {
        id: 'general',
        label: 'General',
        icon: Settings01,
        gate: { permission: TEAMS_ADMIN_PERMISSION },
        component: GeneralSection,
      },
      {
        id: 'team-members',
        label: 'Team Members',
        icon: Users01,
        component: TeamMembersSection,
      },
      {
        id: 'teams',
        label: 'Teams',
        icon: Users01,
        gate: {
          featureFlag: 'team_management',
          permission: TEAMS_ADMIN_PERMISSION,
        },
        component: TeamsSection,
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: CreditCard01,
        gate: { permission: BILLING_ADMIN_PERMISSION },
        component: BillingSection,
      },
    ],
  },
];

/**
 * Hidden sections — accessible via direct URL but not shown in the sidebar.
 * The Advanced/Feature Flags section was moved to the Debug page (/:orgId/debug).
 */
export const SETTINGS_HIDDEN_SECTIONS: SettingsNavConfigItem[] = [
  {
    // Reached from Email & Calendar's "Manage all accounts" link. Registered as
    // a settings section (rather than the standalone /social/connections route
    // it used to be) purely so it keeps the settings chrome: as its own route
    // it rendered with no sidebar, which made it look like the user had been
    // thrown out of settings entirely.
    //
    // Hidden rather than listed: it is a detail page for one link, and adding a
    // ninth ACCOUNT row for it would bury the settings a user actually browses.
    id: 'connected-accounts',
    label: 'Connected Accounts',
    icon: Users01,
    hidden: true,
    component: SocialConnections,
  },
];

/**
 * Flat list of all section IDs (visible + hidden).
 * Used to validate that a URL section param is known.
 */
export const ALL_SECTION_IDS: ReadonlySet<string> = new Set([
  ...SETTINGS_NAV_GROUPS.flatMap(g => g.items.map(i => i.id)),
  ...SETTINGS_HIDDEN_SECTIONS.map(i => i.id),
]);

/**
 * Default section to show when no section param is provided or the param is invalid.
 */
export const DEFAULT_SECTION = 'personal-info';
